import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_SUB_ENTITY_TYPE_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
  UNASSIGN_SUB_ENTITY_TYPE_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'CreateSubEntities',
  mixins: [alert_mixin],
  props: {
    entityConfigId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    selected_sub_entity_id: '',
    sub_entities_loading: false,
    bindings_loading: false,
    binding_sub_entity: false,
    unbinding_child_id: '',
    entity_type_fallback: 'Supplier',
    current_entity: null,
    available_sub_entities: [],
    bindings: []
  }),
  mounted() {
    this.fetch_available_sub_entities();
    this.fetch_current_bindings();
  },
  computed: {
    entity_type_id() {
      return Number(this.$route.params.entityTypeId || 0);
    },
    entity_type_name() {
      return String(
        this.current_entity?.entity_type_name ||
        this.current_entity?.name ||
        this.$route.query.entityTypeName ||
        this.entity_type_fallback ||
        'Entity'
      ).trim();
    },
    bindable_sub_entities() {
      const bound_child_ids = new Set(
        (this.bindings || [])
          .map((item) => String(item.child_entity_type_id || ''))
          .filter(Boolean)
      );

      return (this.available_sub_entities || []).filter(
        (item) => !bound_child_ids.has(String(item.id))
      );
    },
    active_bindings_count() {
      return this.bindings.length;
    },
    entity_overview_stats() {
      const bindings = this.bindings || [];
      return [
        {
          label: 'Configured Children',
          value: bindings.length
        }
      ];
    }
  },
  methods: {
    parse_response_data(payload) {
      if (typeof payload !== 'string') {
        const fallback_payload = payload || {};
        return fallback_payload;
      }

      const trimmed_payload = payload.trim();
      if (!trimmed_payload) {
        const empty_payload = {};
        return empty_payload;
      }

      try {
        return JSON.parse(trimmed_payload);
      } catch {
        const empty_payload = {};
        return empty_payload;
      }
    },
    resolve_binding_id(item) {
      const candidate_values = [
        item?.sub_entity_type_id,
        item?.id,
        item?.rule_id,
        item?.sub_entity_binding_id
      ];
      const resolved_value = candidate_values.find((value) => value !== null && value !== undefined);

      return resolved_value ?? '';
    },
    resolve_binding_reference(sub_entity_type_id, rule_id, child_entity_type_id) {
      if (sub_entity_type_id) {
        return `Binding ID: ${sub_entity_type_id}`;
      }

      if (rule_id) {
        return `Rule ID: ${rule_id}`;
      }

      return `Entity ID: ${child_entity_type_id}`;
    },
    resolve_binding_name(item, index) {
      return item?.child_entity_type_name || item?.name || `Child Entity ${index + 1}`;
    },
    format_available_sub_entity(item, index) {
      const id = String(item?.id ?? '');
      const name = String(
        item?.entity_type_name ||
        item?.name ||
        `Entity Type ${index + 1}`
      ).trim();
      const formatted_sub_entity = { id, name };

      return formatted_sub_entity;
    },
    normalize_binding_source(item, index) {
      const normalized_binding_source = {
        id: String(item?.id ?? item?.rule_id ?? item?.child_entity_type_id ?? index),
        rule_id: String(item?.rule_id ?? ''),
        sub_entity_type_id: String(this.resolve_binding_id(item) || ''),
        child_entity_type_id: String(item?.child_entity_type_id ?? ''),
        child_entity_type_name: String(this.resolve_binding_name(item, index)).trim(),
        child_entity_type_description: String(item?.child_entity_type_description || '').trim()
      };

      return normalized_binding_source;
    },
    is_template_binding_like(item) {
      return item && typeof item === 'object' && (
        'template_id' in item ||
        'template' in item ||
        'template_document_entity_type' in item ||
        'document_name' in item ||
        'document_type' in item
      );
    },
    get_active_template_bindings(config_data) {
      const array_candidates = Object.values(config_data || {}).filter(Array.isArray);
      const binding_array = array_candidates.find((binding_list) =>
        binding_list.some((item) => this.is_template_binding_like(item))
      );
      const template_binding_records = Array.isArray(binding_array) ? binding_array : [];

      return template_binding_records.filter((binding) => binding?.is_active !== false);
    },
    build_binding_configuration_summary(config_data = {}) {
      const form_bindings = Array.isArray(config_data?.form_bindings) ? config_data.form_bindings : [];
      const workflow_bindings = Array.isArray(config_data?.workflow_bindings) ? config_data.workflow_bindings : [];
      const rule_bindings = Array.isArray(config_data?.rule_bindings) ? config_data.rule_bindings : [];
      const template_bindings = this.get_active_template_bindings(config_data);
      const summary_items = [
        {
          key: 'forms',
          label: 'Forms',
          count: form_bindings.length,
          icon: 'bi-file-earmark-text-fill'
        },
        {
          key: 'workflows',
          label: 'Workflows',
          count: workflow_bindings.length,
          icon: 'bi-diagram-3-fill'
        },
        {
          key: 'rules',
          label: 'Rules',
          count: rule_bindings.length,
          icon: 'bi-shield-check'
        },
        {
          key: 'templates',
          label: 'Templates',
          count: template_bindings.length,
          icon: 'bi-file-earmark-richtext-fill'
        }
      ];
      const configured_items = summary_items.filter((item) => item.count > 0);
      const total_configured = summary_items.reduce((total, item) => total + item.count, 0);
      const summary = configured_items.length
        ? configured_items.map((item) => `${item.count} ${item.label}`).join(', ')
        : 'No forms, workflows, templates, or rules bound yet';
      const configuration_summary = {
        entity_config_id: Number(config_data?.entity_config?.id || 0) || null,
        summary_items,
        summary,
        total_configured,
        status_icon: total_configured > 0 ? 'bi-check-circle-fill' : 'bi-x-circle-fill',
        status_text: total_configured > 0 ? 'Configuration linked' : 'No linked configuration yet',
        status_tone_class: total_configured > 0 ? 'text-success' : 'text-danger'
      };

      return configuration_summary;
    },
    format_binding_item(item, index, config_summary = null) {
      const normalized_item = this.normalize_binding_source(item, index);
      const sub_entity_type_id = normalized_item.sub_entity_type_id;
      const rule_id = normalized_item.rule_id;
      const child_entity_type_id = normalized_item.child_entity_type_id;
      const child_entity_type_name = normalized_item.child_entity_type_name;
      const child_entity_type_description = normalized_item.child_entity_type_description;
      const reference = this.resolve_binding_reference(sub_entity_type_id, rule_id, child_entity_type_id);
      const configuration = config_summary || this.build_bindingConfigurationFallback();

      const binding_item = {
        id: normalized_item.id,
        rule_id: String(rule_id || ''),
        sub_entity_type_id: String(sub_entity_type_id || ''),
        child_entity_type_id: String(child_entity_type_id || ''),
        name: String(child_entity_type_name).trim(),
        description: String(child_entity_type_description).trim(),
        reference,
        status: 'ACTIVE',
        entity_config_id: configuration.entity_config_id,
        config_summary_items: configuration.summary_items,
        config_summary_text: configuration.summary,
        total_configured_items: configuration.total_configured,
        configuration_status_icon: configuration.status_icon,
        configuration_status_text: configuration.status_text,
        configuration_status_tone_class: configuration.status_tone_class
      };

      return binding_item;
    },
    build_bindingConfigurationFallback() {
      const configuration_fallback = {
        entity_config_id: null,
        summary_items: [
          { key: 'forms', label: 'Forms', count: 0, icon: 'bi-file-earmark-text-fill' },
          { key: 'workflows', label: 'Workflows', count: 0, icon: 'bi-diagram-3-fill' },
          { key: 'rules', label: 'Rules', count: 0, icon: 'bi-shield-check' },
          { key: 'templates', label: 'Templates', count: 0, icon: 'bi-file-earmark-richtext-fill' }
        ],
        summary: 'No forms, workflows, templates, or rules bound yet',
        total_configured: 0,
        status_icon: 'bi-x-circle-fill',
        status_text: 'No linked configuration yet',
        status_tone_class: 'text-danger'
      };

      return configuration_fallback;
    },
    build_child_requirements_route() {
      const entityTypeId = this.entity_type_id ? String(this.entity_type_id) : '';
      const child_requirements_route = {
        name: 'SubEntityRequirements',
        query: {
          entityTypeId,
          entityTypeName: this.entity_type_name
        }
      };

      return child_requirements_route;
    },
    build_entity_overview_route() {
      const name = 'EntityConfigDetail';
      const entityTypeId = this.entity_type_id;
      const params = { entityTypeId };
      const route = { name, params };

      return route;
    },
    build_child_entity_overview_route(binding) {
      const entityTypeId = Number(binding?.child_entity_type_id || 0);
      const child_route = {
        name: 'EntityConfigDetail',
        params: {
          entityTypeId
        }
      };

      return child_route;
    },
    build_sub_entity_payload() {
      const parent_id = Number(this.entity_type_id);
      const child_id = Number(this.selected_sub_entity_id);

      if (!Number.isInteger(parent_id) || parent_id <= 0) {
        this.show_error('Unable to determine the current entity type.');
        return null;
      }

      if (!Number.isInteger(child_id) || child_id <= 0) {
        this.show_error('Please select a valid child entity type.');
        return null;
      }

      const payload = { parent_id, child_id };
      return payload;
    },
    build_unassign_payload(binding) {
      const parent_id = Number(this.entity_type_id);
      const child_id = Number(binding?.child_entity_type_id);

      if (!Number.isInteger(parent_id) || parent_id <= 0) {
        this.show_error('Unable to determine the current parent entity type.');
        return null;
      }

      if (!Number.isInteger(child_id) || child_id <= 0) {
        this.show_error('Unable to determine the selected child entity type.');
        return null;
      }

      const unassign_payload = { parent_id, child_id };

      return unassign_payload;
    },
    async fetch_full_config_by_entity_type(entity_type_id) {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id
          }
        })
      );
      const response_data = this.parse_response_data(response.data);

      return response_data.data || {};
    },
    async fetch_entity_config_summary(entity_type_id) {
      if (!Number.isInteger(entity_type_id) || entity_type_id <= 0) {
        return this.build_bindingConfigurationFallback();
      }

      const config_data = await this.fetch_full_config_by_entity_type(entity_type_id).catch(() => null);
      if (!config_data) {
        return this.build_bindingConfigurationFallback();
      }

      return this.build_binding_configuration_summary(config_data);
    },
    async fetch_available_sub_entities() {
      this.sub_entities_loading = true;

      try {
        const response = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config());
        const response_data = this.parse_response_data(response.data);
        const response_items = Array.isArray(response_data.data) ? response_data.data : [];

        const current_entity = response_items.find((item) => Number(item?.id) === this.entity_type_id) || null;
        this.current_entity = current_entity;

        this.available_sub_entities = response_items
          .map((item, index) => this.format_available_sub_entity(item, index))
          .filter((item) => item.id)
          .filter((item) => Number(item.id) !== this.entity_type_id);
      } catch {
        this.available_sub_entities = [];
      } finally {
        this.sub_entities_loading = false;
      }
    },
    async fetch_current_bindings() {
      const parent_entity_type_id = Number(this.entity_type_id);

      if (!Number.isInteger(parent_entity_type_id) || parent_entity_type_id <= 0) {
        this.bindings = [];
        return;
      }

      this.bindings_loading = true;

      try {
        const response = await axios.get(
          LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
          get_request_config({
            params: { parent_entity_type_id }
          })
        );
        const response_data = this.parse_response_data(response.data);
        const response_items = Array.isArray(response_data.data) ? response_data.data : [];
        const config_summaries = await Promise.all(
          response_items.map((item) => this.fetch_entity_config_summary(Number(item?.child_entity_type_id || 0)))
        );
        const formatted_bindings = [];

        response_items.forEach((item, index) => {
        const formatted_binding = this.format_binding_item(item,index,Reflect.get(config_summaries, index) ?? null);
        formatted_bindings.push(formatted_binding);
        });

        this.bindings = formatted_bindings;
      } catch {
        this.bindings = [];
      } finally {
        this.bindings_loading = false;
      }
    },
    async bind_sub_entity() {
      const payload = this.build_sub_entity_payload();
      if (!payload) {
        return;
      }

      const selected = this.available_sub_entities.find((item) => item.id === this.selected_sub_entity_id);
      if (!selected) {
        this.show_error('The selected entity type could not be found.');
        return;
      }

      const already_bound = this.bindings.some(
        (item) => String(item.child_entity_type_id) === String(selected.id)
      );
      if (already_bound) {
        this.selected_sub_entity_id = '';
        this.show_error('This child entity type has already been assigned.');
        return;
      }

      this.binding_sub_entity = true;

      try {
        const response = await axios.post(
          CREATE_SUB_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        this.selected_sub_entity_id = '';
        await this.fetch_current_bindings();
        this.show_success(response_data.message || 'Child entity assigned successfully.');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to assign child entity.');
      } finally {
        this.binding_sub_entity = false;
      }
    },
    async unassign_binding(binding) {
      const payload = this.build_unassign_payload(binding);
      if (!payload) {
        return;
      }

      this.unbinding_child_id = String(binding?.id || '');

      try {
        const response = await axios.post(
          UNASSIGN_SUB_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        this.bindings = this.bindings.filter((item) => String(item.id) !== String(binding?.id));
        this.show_success(response_data.message || 'Child entity unassigned successfully.');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to unassign child entity.');
      } finally {
        this.unbinding_child_id = '';
      }
    },
    remove_binding(binding) {
      this.show_confirm(
        'Unassign Child Entity',
        `Are you sure you want to unassign ${binding?.name || 'this child entity'} from ${this.entity_type_name}?`,
        () => {
          this.unassign_binding(binding);
        }
      );
    },
    open_child_entity_config(binding) {
      const child_entity_type_id = Number(binding?.child_entity_type_id || 0);
      if (!Number.isInteger(child_entity_type_id) || child_entity_type_id <= 0) {
        this.show_error('Unable to determine the selected child entity configuration.');
        return;
      }

      this.$router.push(this.build_child_entity_overview_route(binding));
    },
    go_to_entity_overview() {
      if (this.entity_type_id) {
        this.$router.push(this.build_entity_overview_route());
        return;
      }

      this.$router.push({ name: 'EntityConfigs' });
    },
    go_to_child_requirements() {
      this.$router.push(this.build_child_requirements_route());
    }
  }
};
